import { useId, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { compute } from "../lib/compound";

const selectClass =
  "h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const FREQS: ReadonlyArray<{ value: number; label: () => string }> = [
  { value: 1, label: () => m.cp_freq_annually() },
  { value: 4, label: () => m.cp_freq_quarterly() },
  { value: 12, label: () => m.cp_freq_monthly() },
  { value: 365, label: () => m.cp_freq_daily() },
];

function OutputField({
  label,
  value,
  testid,
  emphasis,
}: {
  label: string;
  value: string;
  testid: string;
  emphasis?: boolean;
}) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        readOnly
        data-testid={testid}
        value={value}
        className={`font-mono ${emphasis ? "text-xl font-bold" : "text-lg"}`}
      />
    </div>
  );
}

export function CompoundTool() {
  const uid = useId();
  const [principal, setPrincipal] = useState("1000");
  const [rate, setRate] = useState("5");
  const [years, setYears] = useState("10");
  const [n, setN] = useState("12");

  const result = useMemo(
    () => compute(Number(principal), Number(rate), Number(years), Number(n)),
    [principal, rate, years, n],
  );

  return (
    <div className="space-y-6">
      <div className="grid gap-4 rounded-2xl border border-border bg-card/60 p-6 sm:grid-cols-2 sm:p-8">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-amt`}>{m.cp_principal_label()}</Label>
          <Input
            id={`${uid}-amt`}
            type="text"
            inputMode="decimal"
            value={principal}
            onChange={(e) => setPrincipal(e.target.value)}
            placeholder={m.cp_principal_ph()}
            autoComplete="off"
            className="font-mono text-lg"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-rate`}>{m.cp_rate_label()}</Label>
          <Input
            id={`${uid}-rate`}
            type="text"
            inputMode="decimal"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            autoComplete="off"
            className="font-mono text-lg"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-years`}>{m.cp_years_label()}</Label>
          <Input
            id={`${uid}-years`}
            type="text"
            inputMode="numeric"
            value={years}
            onChange={(e) => setYears(e.target.value)}
            autoComplete="off"
            className="font-mono text-lg"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-freq`}>{m.cp_compound_label()}</Label>
          <select
            id={`${uid}-freq`}
            className={selectClass}
            value={n}
            onChange={(e) => setN(e.target.value)}
          >
            {FREQS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label()}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        {result ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <OutputField
              label={m.cp_balance_out()}
              value={result.finalBalance.toFixed(2)}
              testid="cp-balance"
              emphasis
            />
            <OutputField
              label={m.cp_interest_out()}
              value={result.totalInterest.toFixed(2)}
              testid="cp-interest"
            />
          </div>
        ) : (
          <p data-testid="cp-error" className="text-sm text-muted-foreground">
            {m.cp_invalid()}
          </p>
        )}
      </div>
    </div>
  );
}
